import { Navigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";

const ProtectedAdminRoute = ({ children, requiredRoute }) => {
  const [hasAccess, setHasAccess] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  
  // Log when component mounts/updates
  console.log(`[ProtectedAdminRoute] Component rendered for route: ${requiredRoute}, current path: ${location.pathname}`);

  useEffect(() => {
    const checkAccess = () => {
      try {
        const userData = localStorage.getItem("user");
        if (!userData) {
          console.log(`[ProtectedAdminRoute] ${requiredRoute}: No user data - DENIED`);
          setHasAccess(false);
          setIsLoading(false);
          return;
        }

        const user = JSON.parse(userData);
        console.log(`[ProtectedAdminRoute] ${requiredRoute}: Checking access for user:`, {
          role: user.role,
          hasAdminRole: !!user.adminRole,
          adminRoleName: user.adminRole?.name,
          allowedRoutes: user.adminRole?.allowedRoutes
        });
        
        // Super Admin has access to all routes
        if (user.role === "super admin" || user.role === "super_admin") {
          console.log(`[ProtectedAdminRoute] ${requiredRoute}: Super admin - ALLOWED`);
          setHasAccess(true);
          setIsLoading(false);
          return;
        }

        // If user is admin but has no adminRole assigned, give full access (backward compatibility)
        if (user.role === "admin" && (!user.adminRole || !user.adminRole.allowedRoutes)) {
          console.log(`[ProtectedAdminRoute] ${requiredRoute}: Admin with no role - ALLOWED (backward compat)`);
          setHasAccess(true);
          setIsLoading(false);
          return;
        }
        
        // Check if user has adminRole with allowedRoutes
        if (user.adminRole && typeof user.adminRole === 'object' && user.adminRole.allowedRoutes && Array.isArray(user.adminRole.allowedRoutes)) {
          const allowedRoutes = user.adminRole.allowedRoutes;
          
          if (allowedRoutes.length === 0) {
            console.log(`[ProtectedAdminRoute] ${requiredRoute}: AllowedRoutes is empty - DENIED`);
            setHasAccess(false);
            setIsLoading(false);
            return;
          }
          
          // Check if the required route is in allowedRoutes
          console.log(`[ProtectedAdminRoute] ${requiredRoute}: Starting route check`, {
            allowedRoutes: JSON.stringify(allowedRoutes),
            requiredRoute: requiredRoute,
            allowedRoutesLength: allowedRoutes.length
          });
          
          const hasRouteAccess = allowedRoutes.some((allowedRoute, index) => {
            console.log(`[ProtectedAdminRoute] ${requiredRoute}: Checking route ${index + 1}/${allowedRoutes.length}: "${allowedRoute}"`);
            
            // Exact match
            if (allowedRoute === requiredRoute) {
              console.log(`[ProtectedAdminRoute] ${requiredRoute}: ✓✓✓ EXACT MATCH with "${allowedRoute}" ✓✓✓`);
              return true;
            }
            
            // Special case: if allowedRoute is "/admin", it should ONLY match "/admin" exactly
            // This prevents "/admin" from granting access to all child routes
            if (allowedRoute === "/admin") {
              console.log(`[ProtectedAdminRoute] ${requiredRoute}: ✗ "/admin" only matches exactly, not "${requiredRoute}"`);
              return false;
            }
            
            // Parent route match (e.g., /admin/usage allows /admin/usage/individuals)
            if (requiredRoute.startsWith(allowedRoute + "/")) {
              console.log(`[ProtectedAdminRoute] ${requiredRoute}: ✓✓✓ PARENT MATCH with "${allowedRoute}" ✓✓✓`);
              return true;
            }
            
            console.log(`[ProtectedAdminRoute] ${requiredRoute}: ✗ No match with "${allowedRoute}"`);
            return false;
          });

          console.log(`[ProtectedAdminRoute] ${requiredRoute}: Final access decision: ${hasRouteAccess}`, {
            allowedRoutes: allowedRoutes,
            requiredRoute: requiredRoute,
            currentPath: location.pathname
          });

          setHasAccess(hasRouteAccess);
        } else {
          // AdminRole assigned but has no routes or is invalid - no access
          console.log(`[ProtectedAdminRoute] ${requiredRoute}: AdminRole exists but allowedRoutes is missing/invalid - DENIED`, {
            hasAdminRole: !!user.adminRole,
            adminRoleType: typeof user.adminRole,
            hasAllowedRoutes: !!user.adminRole?.allowedRoutes,
            isArray: Array.isArray(user.adminRole?.allowedRoutes)
          });
          setHasAccess(false);
        }
      } catch (error) {
        console.error(`[ProtectedAdminRoute] ${requiredRoute}: Error checking route access:`, error);
        setHasAccess(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAccess();
  }, [requiredRoute, location.pathname]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-fg-60">
        Loading...
      </div>
    );
  }

  if (!hasAccess) {
    console.log(`[ProtectedAdminRoute] ${requiredRoute}: Access DENIED - redirecting to first allowed route or /admin`);
    // If user has allowedRoutes, redirect to the first one instead of /admin
    try {
      const userData = localStorage.getItem("user");
      if (userData) {
        const user = JSON.parse(userData);
        if (user?.adminRole?.allowedRoutes && Array.isArray(user.adminRole.allowedRoutes) && user.adminRole.allowedRoutes.length > 0) {
          const firstAllowedRoute = user.adminRole.allowedRoutes[0];
          console.log(`[ProtectedAdminRoute] ${requiredRoute}: Redirecting to first allowed route: ${firstAllowedRoute}`);
          return <Navigate to={firstAllowedRoute} replace />;
        }
      }
    } catch (error) {
      console.error(`[ProtectedAdminRoute] Error getting first allowed route:`, error);
    }
    // Fallback to /admin
    return <Navigate to="/admin" replace />;
  }

  return children;
};

export default ProtectedAdminRoute;

